/**
 * Slice LEVI-NAV-01 · Narrow shared workspace-awareness store.
 *
 * Two responsibilities, kept in one small module:
 *   1. Publish sanitized workspace context that useLeviCommand
 *      forwards to the backend on every /api/levi/command call.
 *      Only allowlisted keys are stored — arbitrary DOM / form
 *      contents are rejected (§refinement 6).
 *   2. Track "has_unsaved_edits" per active document so automatic
 *      navigation can pause and ask before discarding work
 *      (§refinement 4). If a page has unsaved edits, Levi presents
 *      a deterministic prompt — never silently discards.
 *
 * Not a persistent store. Just a module-scope Map + React hooks.
 * No LLM. No network. No cross-tab sync.
 */

// ------------------------------------------------------------
// Allowlist (mirror of backend.levi_nav.ALLOWED_WORKSPACE_KEYS)
// ------------------------------------------------------------
export const ALLOWED_WORKSPACE_KEYS = Object.freeze([
  "route_classification",
  "active_project_id",
  "active_customer_id",
  "open_document_type",
  "open_document_id",
  "current_section",
  "has_unsaved_edits",
  "pending_confirmation",
]);

export const ALLOWED_DOCUMENT_TYPES = Object.freeze([
  "estimate", "proposal", "contract", "invoice",
  "work_order", "closeout_packet", "walkthrough",
  "customer", "project",
]);

// ------------------------------------------------------------
// Module-scope store.
// ------------------------------------------------------------
const _hints = new Map(); // hint_id -> { ...allowlisted keys }
const _listeners = new Set();

function _notify() {
  for (const fn of _listeners) {
    try { fn(); } catch (_) { /* ignore */ }
  }
}

function _sanitize(input) {
  const out = {};
  if (!input || typeof input !== "object") return out;
  for (const k of ALLOWED_WORKSPACE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(input, k)) continue;
    const v = input[k];
    if (k === "has_unsaved_edits" || k === "pending_confirmation") {
      out[k] = !!v;
    } else if (k === "open_document_type") {
      if (typeof v === "string" && ALLOWED_DOCUMENT_TYPES.includes(v)) out[k] = v;
    } else if (typeof v === "string" && v.length <= 128) {
      out[k] = v;
    }
  }
  return out;
}

export function registerLeviWorkspaceHint(hintId, payload) {
  _hints.set(hintId, _sanitize(payload));
  _notify();
}

export function clearLeviWorkspaceHint(hintId) {
  _hints.delete(hintId);
  _notify();
}

/** Merge every registered hint into a single sanitized snapshot. Later
 *  registrations override earlier ones for the same key so nested
 *  detail pages naturally shadow the outer workspace hint. */
export function readLeviWorkspaceSnapshot(extra) {
  const merged = {};
  for (const hint of _hints.values()) {
    Object.assign(merged, hint);
  }
  if (extra) Object.assign(merged, _sanitize(extra));
  return _sanitize(merged);
}

export function hasUnsavedEdits() {
  return Object.values(readLeviWorkspaceSnapshot() || {}).length > 0 &&
    !!readLeviWorkspaceSnapshot().has_unsaved_edits;
}

export function subscribeLeviWorkspace(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// ------------------------------------------------------------
// React helper — pages call this once, and updates auto-clean.
// ------------------------------------------------------------
import { useEffect } from "react";

export function useLeviWorkspaceHint(hintId, payload) {
  useEffect(() => {
    if (!hintId) return () => {};
    registerLeviWorkspaceHint(hintId, payload);
    return () => clearLeviWorkspaceHint(hintId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintId, JSON.stringify(payload || {})]);
}
